import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { lintGutter } from '@codemirror/lint';
import { githubDark, githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';

import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { RepoUrlPicker } from '../components';
import { useYamlLinter } from '../hooks/useYamlLinter';
import {
  GIT_WRITE_BACK_INFO,
  IMAGE_UPDATER_INFO,
  IMAGES_INFO,
} from '../infoDoc';
import { imagesSchema } from '../schema/imagesSchema';
import { useGeneratorsStyles, useStyles } from '../styles';
import {
  DEFAULT_GIT_WRITE_BACK_CONFIG,
  DEFAULT_IMAGE_UPDATER_CONFIG,
  GitWriteBackConfig,
  ImageUpdaterConfig,
  SourceConfiguration,
} from '../types';
import { gitWriteBackFieldErrors, imagesInputErrors } from '../validation';

const IMAGES_PLACEHOLDER =
  'images: []';

export interface ImageUpdaterSectionProps {
  value: ImageUpdaterConfig;
  onChange: (value: ImageUpdaterConfig) => void;
  source: SourceConfiguration | null;
  errors: string[];
}

const deriveGitWriteBackFromSource = (
  source: SourceConfiguration | null,
): GitWriteBackConfig => {
  if (!source) return { ...DEFAULT_GIT_WRITE_BACK_CONFIG };

  try {
    const u = new URL(source.repoURL);
    if (u.host === 'github.com') {
      return {
        repository: source.repoURL,
        branch: source.targetRevision,
        target: '',
      };
    }
  } catch {
    // not a URL — fall through
  }

  if (source.helm?.valuesSource) {
    return {
      repository: source.helm.valuesSource.repoURL,
      branch: source.helm.valuesSource.targetRevision,
      target: '',
    };
  }

  return { ...DEFAULT_GIT_WRITE_BACK_CONFIG };
};

const ImageUpdaterSectionInner = ({
  value,
  onChange,
  source,
  errors,
}: ImageUpdaterSectionProps) => {
  const { classes } = useStyles();
  const { classes: generatorClasses, cx } = useGeneratorsStyles();
  const theme = useTheme();
  const sectionRef = useRef<HTMLDivElement>(null);
  const data = value;
  const disabled = !data.enabled;

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;
  const sourceRef = useRef(source);
  sourceRef.current = source;

  const {
    yamlLinter,
    errors: yamlErrors,
    warnings: yamlWarnings,
  } = useYamlLinter(imagesSchema);

  const handleEnabledChange = useCallback((checked: boolean) => {
    if (!checked) {
      onChangeRef.current({ ...DEFAULT_IMAGE_UPDATER_CONFIG, enabled: false });
      return;
    }
    const current = dataRef.current;
    const gitWriteBackConfig =
      current.gitWriteBackConfig ??
      deriveGitWriteBackFromSource(sourceRef.current);
    onChangeRef.current({
      ...current,
      enabled: true,
      gitWriteBackConfig,
    });
  }, []);

  const handleImagesYAMLChange = useCallback((v: string) => {
    onChangeRef.current({ ...dataRef.current, imagesYAML: v });
  }, []);

  const handleGitWriteBackChange = useCallback(
    (patch: Partial<GitWriteBackConfig>) => {
      const current = dataRef.current;
      const base = current.gitWriteBackConfig ?? DEFAULT_GIT_WRITE_BACK_CONFIG;
      onChangeRef.current({
        ...current,
        gitWriteBackConfig: { ...base, ...patch },
      });
    },
    [],
  );

  const imagesError =
    errors.length > 0 && data.enabled ? imagesInputErrors(data.imagesYAML) : '';
  const writeBackErrors = useMemo(
    () => gitWriteBackFieldErrors(data.gitWriteBackConfig),
    [data.gitWriteBackConfig],
  );

  useEffect(() => {
    if (imagesError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [imagesError]);

  const errorCount = yamlErrors?.length ?? 0;
  const warningCount = yamlWarnings?.length ?? 0;

  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Image Updater Configuration"
          description="Configure the ArgoCD Image Updater for automatic container image updates."
          variant="h5"
          divider
          infoDoc={IMAGE_UPDATER_INFO}
        />

        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Enable Image Updater"
            description="Enable or disable the ArgoCD Image Updater for this application."
          />
          <FormControlLabel
            control={
              <Checkbox
								checked={data.enabled}
                color="secondary"
                onChange={e => handleEnabledChange(e.target.checked)}
              />
            }
            label="Enable Image Updater"
          />
        </Box>

        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Git Write-back Configuration"
            description="Where ArgoCD Image Updater should push updates. Auto-filled from the Source section."
            infoDoc={GIT_WRITE_BACK_INFO}
          />
          <RepoUrlPicker
            value={data.gitWriteBackConfig?.repository ?? ''}
            onChange={url => handleGitWriteBackChange({ repository: url })}
            label="Repository"
            disabled={disabled}
            freeSolo={false}
            error={Boolean(writeBackErrors.repository)}
            helperText={writeBackErrors.repository}
          />
        </Box>

        <Box className={classes.inputContainer}>
          <TextField
            label="Revision"
            fullWidth
            variant="outlined"
            size="small"
            margin="none"
            value={data.gitWriteBackConfig?.branch ?? ''}
            onChange={e => handleGitWriteBackChange({ branch: e.target.value })}
            disabled={disabled}
            error={Boolean(writeBackErrors.branch)}
            helperText={writeBackErrors.branch}
          />
        </Box>

        <Box className={classes.inputContainer}>
          <TextField
            label="Write-back Target"
            fullWidth
            variant="outlined"
            size="small"
            margin="none"
            value={data.gitWriteBackConfig?.target ?? ''}
            onChange={e => handleGitWriteBackChange({ target: e.target.value })}
            disabled={disabled}
            helperText="Optional. Use `helmvalues:<path-to-values.yaml>` for Helm value files or `kustomization:<path-to-overlay-directory>` for Kustomize overlays."
          />
        </Box>

        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Images"
            description="Define the container images to be tracked, as YAML. Schema is validated as you type."
            infoDoc={IMAGES_INFO}
          />
        </Box>

        <Box margin={1}>
          <Box
            className={cx(
              generatorClasses.codeMirror,
              disabled && generatorClasses.codeMirrorDisabled,
            )}
            sx={{ height: 200 }}
          >
            <CodeMirror
              value={data.imagesYAML ?? ''}
              editable={!disabled}
              extensions={[yamlLang(), lintGutter(), yamlLinter]}
              onChange={handleImagesYAMLChange}
              theme={theme.palette.mode === 'dark' ? githubDark : githubLight}
              placeholder={IMAGES_PLACEHOLDER}
            />
          </Box>
          <Box className={generatorClasses.validationContainer}>
            <Box
              className={`${generatorClasses.validationCount} ${
                errorCount > 0
                  ? generatorClasses.validationErrorStyle
                  : generatorClasses.validationDefaultStyle
              }`}
              aria-label="error-count"
              title={`${errorCount} error${errorCount === 1 ? '' : 's'}`}
            >
              <ErrorOutline fontSize="small" />
              <span>{errorCount}</span>
            </Box>
            <Box
              className={`${generatorClasses.validationCount} ${
                warningCount > 0
                  ? generatorClasses.validationWarningStyle
                  : generatorClasses.validationDefaultStyle
              }`}
              aria-label="warning-count"
              title={`${warningCount} warning${warningCount === 1 ? '' : 's'}`}
            >
              <ReportProblemOutlined fontSize="small" />
              <span>{warningCount}</span>
            </Box>
          </Box>
          {imagesError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={imagesError} />
            </Box>
          )}
        </Box>
      </ScaffolderFormPaper>
    </div>
  );
};

export const ImageUpdaterSection = memo(ImageUpdaterSectionInner);
